/**
 * Settings → Notifications route.
 *
 * Loads `notifications.getPreferences`, hydrates the company notification
 * matrix, and saves diffs through `notifications.updatePreferences`.
 *
 * @see 08-system-decisions.md §4 — the guardrail contract the switch drives
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { hasPermission } from "@flint/api-contract";
import { trpc } from "../../../../lib/trpc.ts";
import { formatRelativeTime } from "../../../../lib/format.ts";
import { meQueryOptions } from "../../../../lib/session.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { SettingsShell, SaveBar, type SaveBarState } from "../../../../features/settings/index.ts";
import {
  NotificationsForm,
  formFromPreferences,
  valuesToUpdatePayload,
  isDirtyVs,
  EMPTY_NOTIFICATIONS_FORM,
  type NotificationsFormValues,
} from "../../../../features/settings/notifications/index.ts";

export const Route = createFileRoute("/(workspace)/settings/notifications/")({
  // URL-level `settings.manage` gate — see /settings/company/index.tsx for rationale.
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQueryOptions).catch(() => null);
    const perms = me?.company?.permissions ?? [];
    if (!hasPermission(perms, "settings.manage")) {
      throw notFound();
    }
  },
  component: NotificationsSettingsPage,
});

function NotificationsSettingsPage() {
  const utils = trpc.useUtils();
  const preferences = trpc.notifications.getPreferences.useQuery({});

  const [baseline, setBaseline] = useState<NotificationsFormValues>(EMPTY_NOTIFICATIONS_FORM);
  const [values, setValues] = useState<NotificationsFormValues>(EMPTY_NOTIFICATIONS_FORM);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Hydrate when the wire data arrives or refetches. Don't clobber the
  // user's in-flight edit on a background refetch.
  useEffect(() => {
    if (!preferences.data) return;
    const next = formFromPreferences(preferences.data);
    setBaseline(next);
    setValues((current) => (isDirtyVs(current, baseline) ? current : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferences.data]);

  const update = trpc.notifications.updatePreferences.useMutation({
    onSuccess: async () => {
      await utils.notifications.getPreferences.invalidate();
      setSavedAt(new Date());
    },
  });

  const dirty = isDirtyVs(values, baseline);

  const saveBarState: SaveBarState = update.isPending
    ? "saving"
    : dirty
      ? "dirty"
      : savedAt
        ? "saved"
        : "idle";

  const statusText = useMemo(() => {
    if (update.isPending) return "Saving…";
    if (dirty) return "Unsaved changes — review before saving";
    if (savedAt) return `All changes saved · ${formatRelativeTime(savedAt)}`;
    return "No changes yet";
  }, [update.isPending, dirty, savedAt]);

  const onSave = useCallback(async () => {
    const payload = valuesToUpdatePayload(values, baseline);
    if (!payload) return;
    await update.mutateAsync(payload).catch(() => {
      /* surfaced via update.error on the form */
    });
  }, [values, baseline, update]);

  const onDiscard = useCallback(() => {
    setValues(baseline);
  }, [baseline]);

  return (
    <WorkspaceShell breadcrumbs={[{ label: "Settings" }, { label: "Notifications" }]}>
      <SettingsShell
        activeSection="notifications"
        pageTitle="Notifications"
        pageDescription="Control the automated messages Flint sends on your behalf across WhatsApp, email, SMS, and the in-app log."
      >
        {preferences.isLoading ? (
          <p className="text-muted text-sm">Loading notification settings…</p>
        ) : preferences.error ? (
          <Alert
            intent="bad"
            title="Couldn't load notification settings"
            description={preferences.error.message}
          />
        ) : preferences.data ? (
          <>
            <NotificationsForm
              values={values}
              serverError={update.error ? update.error.message : null}
              onChange={setValues}
            />
            <SaveBar
              state={saveBarState}
              statusText={statusText}
              onDiscard={onDiscard}
              onSave={() => void onSave()}
            />
          </>
        ) : null}
      </SettingsShell>
    </WorkspaceShell>
  );
}
